<script setup lang="ts">
  import { computed, watch } from "vue";
  import { useForm } from "vee-validate";
  import { toTypedSchema } from "@vee-validate/zod";
  import { z } from "zod";
  import { FormDialog } from "@/components/episto-ui/form-dialog";
  import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";

  const props = defineProps<{ loading?: boolean; initial?: { name: string; textContent: string } | null }>();
  const open = defineModel<boolean>("open", { default: false });
  const emit = defineEmits<{ save: [name: string, textContent: string] }>();

  const validationSchema = toTypedSchema(
    z.object({
      name: z.string().trim().min(1, "Please enter a name").max(255, "255 characters maximum"),
      textContent: z.string().trim().min(1, "Please paste a text"),
    }),
  );

  const { handleSubmit, resetForm, meta } = useForm({
    validationSchema,
    validateOnMount: false,
    initialValues: { name: "", textContent: "" },
  });

  const editing = computed(() => !!props.initial);

  watch(open, (isOpen) => {
    if (!isOpen) return;
    resetForm({ values: { name: props.initial?.name ?? "", textContent: props.initial?.textContent ?? "" } });
  });

  const handleSave = handleSubmit(({ name, textContent }) => emit("save", name, textContent));
</script>

<template>
  <FormDialog
    v-model:open="open"
    :title="editing ? 'Edit text' : 'Paste text'"
    :confirm-label="editing ? 'Save' : 'Add source'"
    max-width="640px"
    :loading="props.loading"
    :can-save="meta.valid"
    @save="handleSave">
    <FormField
      v-slot="{ componentField }"
      name="name">
      <FormItem>
        <FormLabel>Name</FormLabel>
        <FormControl>
          <Input
            v-bind="componentField"
            placeholder="Kick-off notes" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
    <FormField
      v-slot="{ componentField }"
      name="textContent">
      <FormItem>
        <FormLabel>Text</FormLabel>
        <FormControl>
          <Textarea
            v-bind="componentField"
            class="max-h-[50vh] min-h-40"
            placeholder="Paste the brief, meeting notes or previous results" />
        </FormControl>
        <FormMessage />
      </FormItem>
    </FormField>
  </FormDialog>
</template>
